import { css } from "@emotion/react"
import { Section } from "../../../../packages/editing/Section"
import { useLocalize } from "../../../../packages/localization/client-side/useLocalize"
import { pageWidthCss, responsiveSectionSpacing, useRedoitTheme } from "../ui/theme"
import { Localized } from "../../../../packages/localization/Localized"
import { colors } from "../ui/constants/colors"
import { Flex } from "../ui/components/base/Flex"
import { Heading } from "../ui/components/typography/Heading"
import { Body } from "../ui/components/typography/Body"
import { Box } from "../ui/components/base/Box"
import { responsiveCss } from "../ui/helpers/css"
import { TrustpilotWidget } from "../ui/components/other/TrustpilotWidget"

type TrustpilotCarouselProps = {
    /**
     * Heading for the Trustpilot carousel section.
     * @default '{"no": "Redoit garanterer at du blir fornøyd"}'
     */
    heading?: Localized<string>

    /**
     * Text to show below the heading.
     */
    text?: Localized<string>

    /**
     * Filter the reviews by stars.
     */
    stars?: {
        "5": boolean
        "4": boolean
        "3": boolean
        "2": boolean
        "1": boolean
    }
}
function TrustpilotCarousel(section: TrustpilotCarouselProps) {
    const localize = useLocalize()
    const theme = useRedoitTheme()

    return (
        <div css={responsiveSectionSpacing()}>
            <div
                style={{
                    backgroundColor: colors.gray100,
                    overflow: "hidden",
                    justifyContent: "center",
                }}
                css={css(
                    { display: "flex" },
                    responsiveCss("max", "xs", {
                        borderRadius: theme.borderRadiuses.md.xs,
                        paddingLeft: 16,
                        paddingRight: 16,
                    }),
                    responsiveCss("min", "sm", {
                        width: "100vw",
                        marginLeft: "calc(50% - 50vw)",
                    })
                )}
            >
                <div
                    style={{
                        paddingBottom: 0,
                        paddingTop: !section.heading && !section.text ? 0 : undefined,
                    }}
                    css={css(
                        {
                            display: "flex",
                            flexDirection: "column",
                        },
                        responsiveSectionSpacing(),
                        pageWidthCss()
                    )}
                >
                    {section.heading || section.text ? (
                        <Flex direction="column">
                            {section.heading ? (
                                <Flex alignItems="center" justifyContent="space-between">
                                    <Heading
                                        level={2}
                                        padding={{ bottom: 8 }}
                                        css={responsiveCss("max", "xs", {
                                            lineHeight: "120%",
                                        })}
                                    >
                                        {localize(section.heading)}
                                    </Heading>
                                </Flex>
                            ) : null}
                            {section.text && (
                                <Body size="lg" color="gray400">
                                    {localize(section.text)}
                                </Body>
                            )}
                        </Flex>
                    ) : null}
                    <Box
                        padding={{ y: "xl" }}
                        css={responsiveCss("max", "xs", {
                            marginTop: 32,
                            marginBottom: 40,
                        })}
                    >
                        <Box
                            css={responsiveCss("max", "xs", {
                                scale: "1.15",
                            })}
                        >
                            <TrustpilotWidget
                                widget="carousel"
                                stars={
                                    section.stars
                                        ? Object.entries(section.stars)
                                              .filter(([k, v]) => v)
                                              .map(([k, v]) => Number(k))
                                        : undefined
                                }
                            />
                        </Box>
                    </Box>
                </div>
            </div>
        </div>
    )
}

Section("TrustpilotCarousel", TrustpilotCarousel)
